import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Upload, CreditCard, FileText, MapPin, Building2, Phone, Mail } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { formatPrice, getSessionId, formatAddress } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import { quoteTracker } from '../lib/consultSource';
import { LookCard } from '../components/fabric/LookCard';
import { readLook, clearLook, lookSummary } from '../components/fabric/lookStore';
import { GeneralQuoteRequest } from '../components/website/GeneralQuoteRequest';
import { brandLabel } from '../lib/brandNames';
import { taxLabel } from '../components/tax/taxDisplay';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Company Information
const COMPANY_INFO = {
  name: 'Apollo Shades',
  address: '1 Brooklyn Navy Yard, Building 33, Suite 201B',
  city: 'Brooklyn',
  state: 'NY',
  zip: '11205',
  phone: '(212) 209-0654',
  email: 'info@apolloshades.com',
  website: 'www.apolloshades.com'
};

export const QuotePage = () => {
  const { cart, clearCart, addToCart, cartLoaded, refreshCart } = useCart();
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const [source] = useState(quoteTracker.resolve);
  const [look, setLook] = useState(() => readLook());
  const [formData, setFormData] = useState({
    customer_name: user?.name || '',
    company_name: user?.company || '',
    customer_email: user?.email || '',
    customer_phone: '',
    quote_name: '',  // User-defined name for the quote
    // Billing Address
    billing_address: '',
    billing_city: '',
    billing_state: '',
    billing_zip: '',
    // Shipping/Jobsite Address
    shipping_address: '',
    shipping_city: '',
    shipping_state: '',
    shipping_zip: '',
    same_as_billing: true,
    project_type: 'residential',
    project_stage: 'planning',
    notes: lookSummary(readLook()),
    send_email: true,  // Default to true - always send notification
    send_to_apollo: true,  // Send to Apollo Shades team
  });
  const [loading, setLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [quote, setQuote] = useState(null);

  const hasItems = cart.items?.length > 0;

  const addLookToQuote = async ({ width, height }) => {
    const pricingMaterial = look.light_mode === 'blackout' ? 'blackout' : look.light_mode === 'screen' ? 'solar' : /sheer/i.test(look.light_label || '') ? 'sheer' : 'light-filtering';
    const base = {
      product_type: 'roller-shades', fabric_material: pricingMaterial, fabric_color: look.color_id || look.fabric_code,
      mount_type: 'inside', width, height, control_type: 'continuous-loop', control_position: 'right', bracket_color: 'white',
      roll_type: 'standard', optional_features: [], room_label: `${look.room_name} — ${look.fabric_name}`, quantity: 1, needs_installation: false,
    };
    try {
      const { data } = await axios.post(`${API_URL}/calculate-price`, base);
      const fabricCost = look.price_per_sqft ? (width * height / 144) * Number(look.price_per_sqft) : 0;
      await addToCart({
        ...base,
        fabric_material_name: look.material_name, fabric_collection: look.collection_name, brand_name: look.brand_name,
        fabric_color_name: look.fabric_name, fabric_color_code: look.fabric_code, fabric_image_url: look.fabric_image_url || look.texture_url,
        unit_price: (data.unit_price || 0) + fabricCost, total_price: (data.total_price || 0) + fabricCost,
        from_room_preview: true,
      });
      toast.success(`${look.fabric_name} roller shade added to your quote`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not add this shade');
      throw error;
    }
  };
  const subtotal = cart.total || 0;
  const total = subtotal;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!hasItems) {
      toast.error('Please add items to your quote first');
      return;
    }

    setLoading(true);
    try {
      const params = user ? {} : { session_id: getSessionId() };
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.post(`${API_URL}/quotes/create`, { ...formData, ...source }, { params, headers });
      setQuote(response.data);
      refreshCart();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast.success('Quote created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create quote');
    } finally {
      setLoading(false);
    }
  };

  // Payment goes through the server-priced checkout page (address, tax and confirmed total)
  const handleCheckout = () => {
    if (!hasItems) {
      toast.error('Please add items to your cart first');
      return;
    }
    navigate('/checkout');
  };

  const handleDownloadPDF = async () => {
    if (!quote) return;
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_URL}/quotes/${quote.id}/pdf`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `apollo-quote-${quote.id.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  if (quote) {
    return (
      <Layout>
        <div className="min-h-screen py-12 pt-32 bg-neutral-50" data-testid="quote-success">
          <div className="container-xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto">
              {/* Header */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="w-8 h-8 text-amber-600" />
                </div>
                <h1 className="text-3xl font-serif mb-2">Quote Created!</h1>
                <p className="text-neutral-500">Quote Number: <span className="font-mono font-medium">{quote.id.slice(0, 8).toUpperCase()}</span></p>
              </div>

              {/* Quote Document */}
              <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden shadow-sm">
                {/* Company Header */}
                <div className="bg-neutral-900 text-white p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <h2 className="text-2xl font-serif">{COMPANY_INFO.name}</h2>
                      <p className="text-neutral-400 mt-1">Custom Window Treatments</p>
                    </div>
                    <div className="text-right text-sm">
                      <p>{COMPANY_INFO.address}</p>
                      <p>{COMPANY_INFO.city}, {COMPANY_INFO.state} {COMPANY_INFO.zip}</p>
                      <p className="mt-2">{COMPANY_INFO.phone}</p>
                      <p>{COMPANY_INFO.email}</p>
                    </div>
                  </div>
                </div>

                {/* Quote Info Bar */}
                <div className="bg-amber-50 border-b border-amber-200 px-6 py-3 flex justify-between text-sm">
                  <span><strong>Quote #:</strong> {quote.id.slice(0, 8).toUpperCase()}</span>
                  <span><strong>Date:</strong> {new Date(quote.created_at).toLocaleDateString()}</span>
                  <span><strong>Valid For:</strong> 30 Days</span>
                </div>

                {/* Addresses Section */}
                <div className="grid md:grid-cols-2 gap-6 p-6 border-b border-neutral-200">
                  {/* Bill To */}
                  <div>
                    <h3 className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Building2 className="w-4 h-4" /> Bill To
                    </h3>
                    <div className="bg-neutral-50 p-4 rounded-lg">
                      <p className="font-medium">{quote.customer_name}</p>
                      {quote.company_name && <p className="text-neutral-600">{quote.company_name}</p>}
                      {formatAddress(quote.billing_address) && (
                        <>
                          <p className="text-neutral-600 mt-2">{formatAddress(quote.billing_address)}</p>
                          <p className="text-neutral-600">
                            {[quote.billing_city, quote.billing_state, quote.billing_zip].filter(Boolean).join(', ')}
                          </p>
                        </>
                      )}
                      <p className="text-neutral-600 mt-2">{quote.customer_email}</p>
                      {quote.customer_phone && <p className="text-neutral-600">{quote.customer_phone}</p>}
                    </div>
                  </div>

                  {/* Ship To / Jobsite */}
                  <div>
                    <h3 className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> Ship To / Jobsite
                    </h3>
                    <div className="bg-neutral-50 p-4 rounded-lg">
                      {quote.same_as_billing ? (
                        <p className="text-neutral-500 italic">Same as billing address</p>
                      ) : (
                        <>
                          <p className="font-medium">{quote.customer_name}</p>
                          {quote.shipping_address && (
                            <>
                              <p className="text-neutral-600 mt-2">{quote.shipping_address}</p>
                              <p className="text-neutral-600">
                                {[quote.shipping_city, quote.shipping_state, quote.shipping_zip].filter(Boolean).join(', ')}
                              </p>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Line Items */}
                <div className="p-6">
                  <h3 className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-4">Items</h3>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-neutral-200 text-left text-xs font-medium text-neutral-500 uppercase">
                        <th className="pb-3">Description</th>
                        <th className="pb-3 text-center">Size</th>
                        <th className="pb-3 text-center">Qty</th>
                        <th className="pb-3 text-right">Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quote.items?.map((item, idx) => (
                        <tr key={idx} className="border-b border-neutral-100">
                          <td className="py-3">
                            <p className="font-medium">{item.config?.room_label || item.config?.product_type?.replace('-', ' ')}</p>
                            <p className="text-sm text-neutral-500">
                              {[brandLabel(item.config?.fabric_brand_name), item.config?.fabric_collection_name, item.config?.fabric_color_name].filter(Boolean).join(' · ') || 'Custom fabric'}
                              {item.config?.control_type === 'motorized' && ' (Motorized)'}
                              {item.config?.needs_installation && ' + Installation'}
                            </p>
                          </td>
                          <td className="py-3 text-center text-sm">{item.config?.width}" × {item.config?.height}"</td>
                          <td className="py-3 text-center">{item.quantity}</td>
                          <td className="py-3 text-right font-mono">{formatPrice(item.config?.total_price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Totals */}
                  <div className="mt-6 flex justify-end">
                    <div className="w-64 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-500">Subtotal</span>
                        <span className="font-mono">{formatPrice(quote.subtotal)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-500" data-testid="quote-tax-label">{taxLabel(quote)}</span>
                        <span className="font-mono" data-testid="quote-tax-amount">{quote.tax_details?.status === 'pending_address' ? 'TBC' : formatPrice(quote.tax)}</span>
                      </div>
                      <div className="flex justify-between text-lg font-medium pt-2 border-t border-neutral-200">
                        <span>Total</span>
                        <span className="font-mono">{formatPrice(quote.total)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="bg-neutral-50 p-6 border-t border-neutral-200">
                  <p className="text-sm text-neutral-600 text-center">
                    Thank you for choosing {COMPANY_INFO.name}! This quote is valid for 30 days. 
                    Contact us at {COMPANY_INFO.phone} or {COMPANY_INFO.email} with any questions.
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                <Button onClick={handleDownloadPDF} className="btn-primary" data-testid="download-pdf-btn">
                  <FileText className="w-4 h-4 mr-2" /> Download PDF
                </Button>
                <Button onClick={() => navigate('/products')} variant="outline">Continue Shopping</Button>
              </div>
            </motion.div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!quote && !cartLoaded && !look) {
    return <Layout><div className="min-h-[60vh] pt-32 flex justify-center text-sm text-neutral-400" data-testid="quote-loading">Loading…</div></Layout>;
  }

  if (!quote && cartLoaded && !hasItems && !look) {
    return (
      <Layout>
        <div className="container-xl pt-28 pb-20"><GeneralQuoteRequest user={user} token={token} source={source} /></div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Back Button */}
      <div className="container-xl pt-24 pb-2">
        <button 
          onClick={() => navigate(-1)} 
          className="inline-flex items-center text-neutral-500 hover:text-neutral-900 transition-colors"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
          Back
        </button>
      </div>

      {/* Hero */}
      <section className="pt-8 pb-12 bg-neutral-50">
        <div className="container-xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <span className="label-overline text-amber-700">Get Started</span>
            <h1 className="mt-4 text-neutral-900">Request a Custom Quote</h1>
            <p className="mt-6 text-xl text-neutral-600 leading-relaxed">
              Fill in your project details below and our team will prepare a detailed quote for your custom window treatments.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form */}
      <section className="section-padding">
        <div className="container-xl">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-8" data-testid="quote-form">
                {/* Quote Name */}
                <div>
                  <h3 className="text-xl mb-6">Quote Details</h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="label-overline">Quote Name / Project Name</Label>
                      <Input 
                        value={formData.quote_name} 
                        onChange={(e) => setFormData({ ...formData, quote_name: e.target.value })} 
                        className="mt-2" 
                        placeholder="e.g., Living Room Renovation, Master Bedroom, Office Project"
                        data-testid="quote-name"
                      />
                      <p className="text-sm text-neutral-500 mt-1">Give your quote a memorable name to easily find it later</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl mb-6">Contact Information</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="label-overline">Full Name *</Label>
                      <Input value={formData.customer_name} onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })} required className="mt-2" data-testid="customer-name" />
                    </div>
                    <div>
                      <Label className="label-overline">Company Name</Label>
                      <Input value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} className="mt-2" />
                    </div>
                    <div>
                      <Label className="label-overline">Email *</Label>
                      <Input type="email" value={formData.customer_email} onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })} required className="mt-2" data-testid="customer-email" />
                    </div>
                    <div>
                      <Label className="label-overline">Phone</Label>
                      <Input type="tel" value={formData.customer_phone} onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })} className="mt-2" />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl mb-6">Billing Address</h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="label-overline">Street Address</Label>
                      <Input 
                        value={formData.billing_address} 
                        onChange={(e) => setFormData({ ...formData, billing_address: e.target.value })} 
                        className="mt-2" 
                        placeholder="123 Main Street, Apt 4B"
                      />
                    </div>
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div>
                        <Label className="label-overline">City</Label>
                        <Input 
                          value={formData.billing_city} 
                          onChange={(e) => setFormData({ ...formData, billing_city: e.target.value })} 
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label className="label-overline">State</Label>
                        <Input 
                          value={formData.billing_state} 
                          onChange={(e) => setFormData({ ...formData, billing_state: e.target.value })} 
                          className="mt-2"
                          placeholder="NY"
                        />
                      </div>
                      <div>
                        <Label className="label-overline">ZIP Code</Label>
                        <Input 
                          value={formData.billing_zip} 
                          onChange={(e) => setFormData({ ...formData, billing_zip: e.target.value })} 
                          className="mt-2"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl">Shipping / Jobsite Address</h3>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData.same_as_billing}
                        onCheckedChange={(checked) => setFormData({ ...formData, same_as_billing: checked })}
                      />
                      <span className="text-sm text-neutral-600">Same as billing</span>
                    </label>
                  </div>
                  
                  {!formData.same_as_billing && (
                    <div className="space-y-4">
                      <div>
                        <Label className="label-overline">Street Address</Label>
                        <Input 
                          value={formData.shipping_address} 
                          onChange={(e) => setFormData({ ...formData, shipping_address: e.target.value })} 
                          className="mt-2" 
                          placeholder="123 Main Street, Apt 4B"
                        />
                      </div>
                      <div className="grid sm:grid-cols-3 gap-4">
                        <div>
                          <Label className="label-overline">City</Label>
                          <Input 
                            value={formData.shipping_city} 
                            onChange={(e) => setFormData({ ...formData, shipping_city: e.target.value })} 
                            className="mt-2"
                          />
                        </div>
                        <div>
                          <Label className="label-overline">State</Label>
                          <Input 
                            value={formData.shipping_state} 
                            onChange={(e) => setFormData({ ...formData, shipping_state: e.target.value })} 
                            className="mt-2"
                            placeholder="NY"
                          />
                        </div>
                        <div>
                          <Label className="label-overline">ZIP Code</Label>
                          <Input 
                            value={formData.shipping_zip} 
                            onChange={(e) => setFormData({ ...formData, shipping_zip: e.target.value })} 
                            className="mt-2"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="text-xl mb-6">Project Details</h3>
                  <div className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="label-overline">Project Type</Label>
                        <select value={formData.project_type} onChange={(e) => setFormData({ ...formData, project_type: e.target.value })} className="w-full mt-2 input-form">
                          <option value="residential">Residential</option>
                          <option value="commercial">Commercial</option>
                        </select>
                      </div>
                      <div>
                        <Label className="label-overline">Project Stage</Label>
                        <select value={formData.project_stage} onChange={(e) => setFormData({ ...formData, project_stage: e.target.value })} className="w-full mt-2 input-form">
                          <option value="planning">Planning / Research</option>
                          <option value="ready-to-order">Ready to Order</option>
                          <option value="new-construction">New Construction</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <Label className="label-overline">Additional Notes</Label>
                      <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={4} className="mt-2" placeholder="Describe your project, special requirements, or questions..." />
                    </div>
                  </div>
                </div>

                {look && (
                  <LookCard
                    look={look}
                    onAddToQuote={addLookToQuote}
                    onRemove={() => {
                      clearLook();
                      setLook(null);
                      setFormData(f => ({ ...f, notes: f.notes.replace(lookSummary(look), '').trim() }));
                    }}
                  />
                )}

                {!hasItems && (
                  <div className="bg-amber-50 border border-amber-200 p-6">
                    <p className="text-amber-800 font-medium mb-2">No products configured yet</p>
                    <p className="text-amber-700 text-sm mb-4">Configure your window treatments to get an instant estimate, or submit this form and our team will help you with product selection.</p>
                    <Button type="button" onClick={() => navigate('/products')} variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-100">
                      Configure Products <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                )}

                {/* Notification Options */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                  <h4 className="font-medium text-neutral-900">Quote Delivery Options</h4>
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="send_email"
                      checked={formData.send_email}
                      onCheckedChange={(checked) => setFormData({ ...formData, send_email: checked })}
                    />
                    <label htmlFor="send_email" className="text-sm text-neutral-700 cursor-pointer">
                      Send a copy of the quote to my email
                    </label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="send_to_apollo"
                      checked={formData.send_to_apollo}
                      onCheckedChange={(checked) => setFormData({ ...formData, send_to_apollo: checked })}
                    />
                    <label htmlFor="send_to_apollo" className="text-sm text-neutral-700 cursor-pointer">
                      <strong>Send to Apollo Shades team</strong> for review and follow-up
                    </label>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Button type="submit" disabled={loading || !hasItems} className="btn-primary" data-testid="submit-quote-btn">
                    <FileText className="w-4 h-4 mr-2" />
                    {loading ? 'Submitting...' : 'Save & Send Quote'}
                  </Button>
                </div>
              </form>
            </div>

            {/* Summary Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-neutral-50 p-6 sticky top-24">
                <h3 className="text-xl mb-6">Quote Summary</h3>
                
                {hasItems ? (
                  <>
                    <div className="space-y-4 mb-6">
                      {cart.items.map((item) => (
                        <div key={item.id} className="flex justify-between text-sm">
                          <div>
                            <p className="font-medium">{item.config.room_label || item.config.product_type.replace('-', ' ')}</p>
                            <p className="text-neutral-500">{item.config.width}" × {item.config.height}" × {item.quantity}</p>
                          </div>
                          <p className="font-mono">{formatPrice(item.config.total_price)}</p>
                        </div>
                      ))}
                    </div>
                    <div className="border-t pt-4 space-y-2">
                      <div className="flex justify-between text-sm"><span className="text-neutral-500">Subtotal</span><span className="font-mono">{formatPrice(subtotal)}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-neutral-500">Sales tax</span><span className="text-xs text-neutral-500 text-right" data-testid="quote-est-tax">Calculated from your delivery address</span></div>
                      <div className="flex justify-between font-medium text-lg pt-2 border-t"><span>Subtotal before tax</span><span className="font-mono">{formatPrice(total)}</span></div>
                    </div>
                    
                    <div className="mt-6 pt-6 border-t">
                      <Button 
                        onClick={handleCheckout} 
                        disabled={checkoutLoading || !formData.customer_email || !formData.customer_name}
                        className="w-full bg-green-600 hover:bg-green-700 text-white"
                        data-testid="checkout-btn"
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        {checkoutLoading ? 'Processing...' : 'Proceed to Checkout'}
                      </Button>
                      <p className="text-xs text-neutral-500 text-center mt-2">
                        Secure payment powered by Stripe
                      </p>
                    </div>
                  </>
                ) : (
                  <p className="text-neutral-500 text-sm">No products added yet. Configure your window treatments to see pricing.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};
